import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import axios from 'axios';
import EmailCapture from '../components/EmailCapture';
import { useIsMobile } from '../hooks/useWindowWidth';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5001';

const fmt = (n) => '$' + Math.round(n).toLocaleString();

function CalcCard({ title, icon, desc, children }) {
  return (
    <div style={styles.card}>
      <div style={styles.cardHead}>
        <span style={styles.cardIcon}>{icon}</span>
        <div>
          <h2 style={styles.cardTitle}>{title}</h2>
          <p style={styles.cardDesc}>{desc}</p>
        </div>
      </div>
      {children}
    </div>
  );
}

function Field({ label, prefix, suffix, value, onChange }) {
  return (
    <div style={styles.field}>
      <label style={styles.label}>{label}</label>
      <div style={styles.inputWrap}>
        {prefix && <span style={styles.addon}>{prefix}</span>}
        <input
          style={{ ...styles.input, ...(prefix ? { paddingLeft: '22px' } : {}) }}
          type="number"
          value={value}
          onChange={e => onChange(parseFloat(e.target.value) || 0)}
        />
        {suffix && <span style={styles.suffix}>{suffix}</span>}
      </div>
    </div>
  );
}

function Result({ children }) {
  return <div style={styles.result}>{children}</div>;
}
function BigNum({ children, color }) {
  return <div style={{ ...styles.bigNum, color: color || '#38bdf8' }}>{children}</div>;
}

// ── RETIREMENT ────────────────────────────────────────────────────────────────
export function RetirementCalc() {
  const [f, setF] = useState({ currentAge: 30, retireAge: 65, currentSavings: 10000, monthlyContribution: 500, annualReturn: 7 });
  const [r, setR] = useState(null);
  const [loading, setLoading] = useState(false);

  const calc = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/api/calc/retirement`, f);
      setR(res.data);
    } catch { setR({ error: 'Calculation failed' }); }
    setLoading(false);
  };

  return (
    <CalcCard icon="📊" title="Retirement Calculator" desc="How much will you have when you retire?">
      <div style={styles.grid}>
        <Field label="Current Age" suffix="yrs" value={f.currentAge} onChange={v => setF({ ...f, currentAge: v })} />
        <Field label="Retirement Age" suffix="yrs" value={f.retireAge} onChange={v => setF({ ...f, retireAge: v })} />
        <Field label="Current Savings" prefix="$" value={f.currentSavings} onChange={v => setF({ ...f, currentSavings: v })} />
        <Field label="Monthly Contribution" prefix="$" value={f.monthlyContribution} onChange={v => setF({ ...f, monthlyContribution: v })} />
        <Field label="Expected Annual Return" suffix="%" value={f.annualReturn} onChange={v => setF({ ...f, annualReturn: v })} />
      </div>
      <button style={styles.btn} onClick={calc} disabled={loading}>{loading ? 'Calculating...' : 'Calculate'}</button>
      {r && !r.error && (
        <Result>
          <BigNum>{fmt(r.projectedSavings)}</BigNum>
          <p style={styles.desc}>Projected at retirement in <strong>{r.yearsToRetirement} years</strong></p>
          <div style={styles.breakdown}>
            <span>💰 You contribute: <strong>{fmt(r.totalContributed)}</strong></span>
            <span>📈 Growth from returns: <strong style={{ color: '#22c55e' }}>{fmt(r.growthFromReturns)}</strong></span>
          </div>
        </Result>
      )}
      {r?.error && <div style={styles.errMsg}>{r.error}</div>}
    </CalcCard>
  );
}

// ── DEBT PAYOFF ───────────────────────────────────────────────────────────────
export function DebtCalc() {
  const [f, setF] = useState({ balance: 10000, interestRate: 20, monthlyPayment: 300 });
  const [r, setR] = useState(null);
  const [loading, setLoading] = useState(false);

  const calc = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/api/calc/debt`, f);
      setR(res.data);
    } catch { setR({ error: 'Calculation failed' }); }
    setLoading(false);
  };

  return (
    <CalcCard icon="💳" title="Debt Payoff Calculator" desc="How long until you're debt free?">
      <div style={styles.grid}>
        <Field label="Total Debt Balance" prefix="$" value={f.balance} onChange={v => setF({ ...f, balance: v })} />
        <Field label="Interest Rate" suffix="%" value={f.interestRate} onChange={v => setF({ ...f, interestRate: v })} />
        <Field label="Monthly Payment" prefix="$" value={f.monthlyPayment} onChange={v => setF({ ...f, monthlyPayment: v })} />
      </div>
      <button style={styles.btn} onClick={calc} disabled={loading}>{loading ? 'Calculating...' : 'Calculate'}</button>
      {r && !r.error && (
        <Result>
          <BigNum>{r.yearsToPayoff} <span style={{ fontSize: '24px', fontWeight: 500 }}>yrs</span></BigNum>
          <p style={styles.desc}>Debt free in <strong>{r.monthsToPayoff} months</strong></p>
          <div style={styles.breakdown}>
            <span>💸 Total interest: <strong style={{ color: '#f87171' }}>{fmt(r.totalInterestPaid)}</strong></span>
            <span>💰 Total paid: <strong>{fmt(r.totalPaid)}</strong></span>
          </div>
        </Result>
      )}
      {r?.error && <div style={styles.errMsg}>{r.error}</div>}
    </CalcCard>
  );
}

// ── INVESTMENT GROWTH ─────────────────────────────────────────────────────────
export function InvestmentCalc() {
  const [f, setF] = useState({ principal: 5000, monthlyAddition: 200, years: 20, annualReturn: 8 });
  const [r, setR] = useState(null);
  const [loading, setLoading] = useState(false);

  const calc = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/api/calc/investment`, f);
      setR(res.data);
    } catch { setR({ error: 'Calculation failed' }); }
    setLoading(false);
  };

  return (
    <CalcCard icon="📈" title="Investment Growth Calculator" desc="Watch your money grow over time with regular contributions.">
      <div style={styles.grid}>
        <Field label="Starting Amount" prefix="$" value={f.principal} onChange={v => setF({ ...f, principal: v })} />
        <Field label="Monthly Addition" prefix="$" value={f.monthlyAddition} onChange={v => setF({ ...f, monthlyAddition: v })} />
        <Field label="Time Period" suffix="yrs" value={f.years} onChange={v => setF({ ...f, years: v })} />
        <Field label="Expected Return" suffix="%" value={f.annualReturn} onChange={v => setF({ ...f, annualReturn: v })} />
      </div>
      <div style={styles.returnHints}>
        <span style={styles.hint}>S&P 500 avg: ~10%</span>
        <span style={styles.hint}>Bonds avg: ~4-5%</span>
        <span style={styles.hint}>Savings account: ~4-5%</span>
      </div>
      <button style={styles.btn} onClick={calc} disabled={loading}>{loading ? 'Calculating...' : 'Calculate'}</button>
      {r && !r.error && (
        <Result>
          <BigNum>{fmt(r.futureValue)}</BigNum>
          <p style={styles.desc}>After <strong>{f.years} years</strong> · {r.growthPercent}% total growth</p>
          <div style={styles.breakdown}>
            <span>💵 You contributed: <strong>{fmt(r.totalContributed)}</strong></span>
            <span>📈 Investment growth: <strong style={{ color: '#22c55e' }}>{fmt(r.totalGrowth)}</strong></span>
          </div>
        </Result>
      )}
      {r?.error && <div style={styles.errMsg}>{r.error}</div>}
    </CalcCard>
  );
}

// ── COMPOUND INTEREST ─────────────────────────────────────────────────────────
export function CompoundCalc() {
  const [f, setF] = useState({ principal: 10000, rate: 5, years: 10, compoundsPerYear: 12 });
  const [r, setR] = useState(null);
  const [loading, setLoading] = useState(false);

  const calc = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/api/calc/compound`, f);
      setR(res.data);
    } catch { setR({ error: 'Calculation failed' }); }
    setLoading(false);
  };

  return (
    <CalcCard icon="🔁" title="Compound Interest Calculator" desc="See the magic of compound interest over time.">
      <div style={styles.grid}>
        <Field label="Principal" prefix="$" value={f.principal} onChange={v => setF({ ...f, principal: v })} />
        <Field label="Annual Interest Rate" suffix="%" value={f.rate} onChange={v => setF({ ...f, rate: v })} />
        <Field label="Time Period" suffix="yrs" value={f.years} onChange={v => setF({ ...f, years: v })} />
        <Field label="Compounds Per Year" value={f.compoundsPerYear} onChange={v => setF({ ...f, compoundsPerYear: v })} />
      </div>
      <div style={styles.returnHints}>
        <span style={styles.hint}>Daily = 365 · Monthly = 12 · Quarterly = 4 · Yearly = 1</span>
      </div>
      <button style={styles.btn} onClick={calc} disabled={loading}>{loading ? 'Calculating...' : 'Calculate'}</button>
      {r && !r.error && (
        <Result>
          <BigNum>{fmt(r.finalAmount)}</BigNum>
          <p style={styles.desc}>Interest earned: <strong style={{ color: '#22c55e' }}>{fmt(r.totalInterest)}</strong> · Effective rate: {r.effectiveRate}%/yr</p>
        </Result>
      )}
      {r?.error && <div style={styles.errMsg}>{r.error}</div>}
    </CalcCard>
  );
}

// ── EMERGENCY FUND ─────────────────────────────────────────────────────────────
export function EmergencyCalc() {
  const [f, setF] = useState({ monthlyExpenses: 3000, months: 6 });
  const [r, setR] = useState(null);
  const [loading, setLoading] = useState(false);

  const calc = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/api/calc/emergency`, f);
      setR(res.data);
    } catch { setR({ error: 'Calculation failed' }); }
    setLoading(false);
  };

  return (
    <CalcCard icon="🛡️" title="Emergency Fund Calculator" desc="How much should you have saved for emergencies?">
      <div style={styles.grid}>
        <Field label="Monthly Expenses" prefix="$" value={f.monthlyExpenses} onChange={v => setF({ ...f, monthlyExpenses: v })} />
        <Field label="Months of Coverage" suffix="mo" value={f.months} onChange={v => setF({ ...f, months: v })} />
      </div>
      <div style={styles.returnHints}>
        <span style={styles.hint}>Recommended: 3-6 months · Job insecurity: 6-12 months</span>
      </div>
      <button style={styles.btn} onClick={calc} disabled={loading}>{loading ? 'Calculating...' : 'Calculate'}</button>
      {r && !r.error && (
        <Result>
          <BigNum>{fmt(r.target)}</BigNum>
          <p style={styles.desc}>Target emergency fund for <strong>{r.months} months</strong> of expenses</p>
        </Result>
      )}
      {r?.error && <div style={styles.errMsg}>{r.error}</div>}
    </CalcCard>
  );
}

// ── MORTGAGE ─────────────────────────────────────────────────────────────────
export function MortgageCalc() {
  const [f, setF] = useState({ homePrice: 350000, downPayment: 70000, interestRate: 7.0, loanTermYears: 30, propertyTax: 3500, insurance: 1200 });
  const [r, setR] = useState(null);

  const calc = () => {
    const principal = f.homePrice - f.downPayment;
    const monthlyRate = f.interestRate / 100 / 12;
    const n = f.loanTermYears * 12;
    const monthlyPayment = principal * (monthlyRate * Math.pow(1 + monthlyRate, n)) / (Math.pow(1 + monthlyRate, n) - 1);
    const totalPaid = monthlyPayment * n;
    const totalInterest = totalPaid - principal;
    const monthlyTaxIns = (f.propertyTax + f.insurance) / 12;
    const totalMonthly = monthlyPayment + monthlyTaxIns;
    setR({ principal, monthlyPayment, totalMonthly, totalPaid, totalInterest, monthlyTaxIns, downPct: Math.round(f.downPayment / f.homePrice * 100) });
  };

  return (
    <CalcCard icon="🏠" title="Mortgage Calculator" desc="Estimate your monthly payment and total cost of buying a home.">
      <div style={styles.grid}>
        <Field label="Home Price" prefix="$" value={f.homePrice} onChange={v => setF({ ...f, homePrice: v })} />
        <Field label="Down Payment" prefix="$" value={f.downPayment} onChange={v => setF({ ...f, downPayment: v })} />
        <Field label="Interest Rate" suffix="%" value={f.interestRate} onChange={v => setF({ ...f, interestRate: v })} />
        <Field label="Loan Term" suffix="yrs" value={f.loanTermYears} onChange={v => setF({ ...f, loanTermYears: v })} />
        <Field label="Annual Property Tax" prefix="$" value={f.propertyTax} onChange={v => setF({ ...f, propertyTax: v })} />
        <Field label="Annual Insurance" prefix="$" value={f.insurance} onChange={v => setF({ ...f, insurance: v })} />
      </div>
      <div style={styles.returnHints}>
        <span style={styles.hint}>20% down avoids PMI · Current 30yr avg: ~6.8-7.2%</span>
      </div>
      <button style={styles.btn} onClick={calc}>Calculate</button>
      {r && (
        <div style={styles.result}>
          <BigNum color="#22c55e">{fmt(r.totalMonthly)}<span style={{ fontSize: '20px', fontWeight: 500 }}>/mo</span></BigNum>
          <p style={styles.desc}>Total monthly payment (including taxes & insurance)</p>
          <div style={styles.breakdown}>
            <span>🏦 Principal + Interest: <strong>{fmt(r.monthlyPayment)}/mo</strong></span>
            <span>🏡 Tax + Insurance: <strong>{fmt(r.monthlyTaxIns)}/mo</strong></span>
          </div>
          <div style={{ ...styles.breakdown, marginTop: '10px' }}>
            <span>💰 Loan amount: <strong>{fmt(r.principal)}</strong></span>
            <span>💸 Total interest over {f.loanTermYears} yrs: <strong style={{ color: '#f87171' }}>{fmt(r.totalInterest)}</strong></span>
            <span>📊 Down payment: <strong>{r.downPct}%</strong></span>
          </div>
        </div>
      )}
    </CalcCard>
  );
}

// ── NET WORTH ─────────────────────────────────────────────────────────────────
export function NetWorthCalc() {
  const [assets, setAssets] = useState({ checking: 5000, savings: 10000, investments: 25000, retirement: 30000, homeValue: 0, carValue: 5000, other: 0 });
  const [debts, setDebts] = useState({ creditCards: 3000, carLoan: 8000, studentLoans: 15000, mortgage: 0, otherDebt: 0 });
  const [r, setR] = useState(null);

  const calc = () => {
    const totalAssets = Object.values(assets).reduce((a, b) => a + b, 0);
    const totalDebts = Object.values(debts).reduce((a, b) => a + b, 0);
    const netWorth = totalAssets - totalDebts;
    setR({ totalAssets, totalDebts, netWorth });
  };

  return (
    <CalcCard icon="📊" title="Net Worth Calculator" desc="Assets minus liabilities = your real financial picture.">
      <div style={styles.sectionLabel}>💚 Assets (What You Own)</div>
      <div style={styles.grid}>
        <Field label="Checking/Cash" prefix="$" value={assets.checking} onChange={v => setAssets({ ...assets, checking: v })} />
        <Field label="Savings" prefix="$" value={assets.savings} onChange={v => setAssets({ ...assets, savings: v })} />
        <Field label="Investments" prefix="$" value={assets.investments} onChange={v => setAssets({ ...assets, investments: v })} />
        <Field label="Retirement Accounts" prefix="$" value={assets.retirement} onChange={v => setAssets({ ...assets, retirement: v })} />
        <Field label="Home Value" prefix="$" value={assets.homeValue} onChange={v => setAssets({ ...assets, homeValue: v })} />
        <Field label="Vehicle Value" prefix="$" value={assets.carValue} onChange={v => setAssets({ ...assets, carValue: v })} />
        <Field label="Other Assets" prefix="$" value={assets.other} onChange={v => setAssets({ ...assets, other: v })} />
      </div>
      <div style={styles.sectionLabel}>🔴 Liabilities (What You Owe)</div>
      <div style={styles.grid}>
        <Field label="Credit Card Debt" prefix="$" value={debts.creditCards} onChange={v => setDebts({ ...debts, creditCards: v })} />
        <Field label="Car Loan" prefix="$" value={debts.carLoan} onChange={v => setDebts({ ...debts, carLoan: v })} />
        <Field label="Student Loans" prefix="$" value={debts.studentLoans} onChange={v => setDebts({ ...debts, studentLoans: v })} />
        <Field label="Mortgage Balance" prefix="$" value={debts.mortgage} onChange={v => setDebts({ ...debts, mortgage: v })} />
        <Field label="Other Debt" prefix="$" value={debts.otherDebt} onChange={v => setDebts({ ...debts, otherDebt: v })} />
      </div>
      <button style={styles.btn} onClick={calc}>Calculate Net Worth</button>
      {r && (
        <div style={styles.result}>
          <BigNum color={r.netWorth >= 0 ? '#22c55e' : '#f87171'}>{fmt(r.netWorth)}</BigNum>
          <p style={styles.desc}>Your net worth — update it every month to track progress</p>
          <div style={styles.breakdown}>
            <span>💚 Total assets: <strong style={{ color: '#22c55e' }}>{fmt(r.totalAssets)}</strong></span>
            <span>🔴 Total debts: <strong style={{ color: '#f87171' }}>{fmt(r.totalDebts)}</strong></span>
          </div>
        </div>
      )}
    </CalcCard>
  );
}

// ── SAVINGS RATE ──────────────────────────────────────────────────────────────
export function SavingsRateCalc() {
  const [f, setF] = useState({ monthlyIncome: 5000, housing: 1500, food: 600, transport: 400, utilities: 200, insurance: 300, subscriptions: 100, entertainment: 200, other: 300, savings: 600 });
  const [r, setR] = useState(null);

  const calc = () => {
    const totalSpending = f.housing + f.food + f.transport + f.utilities + f.insurance + f.subscriptions + f.entertainment + f.other;
    const savingsRate = Math.round((f.savings / f.monthlyIncome) * 100);
    const leftover = f.monthlyIncome - totalSpending - f.savings;
    let yearsToFI = null;
    if (savingsRate > 0 && savingsRate < 100) {
      // Rule of thumb: years to FI = 1 / savings_rate × 25 / (1/savings_rate - 1) simplified
      const wr = 0.04; // eslint-disable-line no-unused-vars
      yearsToFI = Math.round(Math.log(25 * f.savings / (f.monthlyIncome * 12)) / Math.log(1.07) + 1);
      // Simpler: based on spending, years ≈ years to have 25x annual spending
      const annualSpending = totalSpending * 12;
      const target = annualSpending * 25;
      let years = 0;
      let balance = 0;
      while (balance < target && years < 100) {
        balance = balance * 1.07 + f.savings * 12;
        years++;
      }
      yearsToFI = years;
    }
    setR({ savingsRate, totalSpending, leftover, yearsToFI });
  };

  return (
    <CalcCard icon="💰" title="Savings Rate Calculator" desc="Your savings rate is the most important number in personal finance.">
      <div style={styles.grid}>
        <Field label="Monthly Take-Home Income" prefix="$" value={f.monthlyIncome} onChange={v => setF({ ...f, monthlyIncome: v })} />
        <Field label="Monthly Savings/Investing" prefix="$" value={f.savings} onChange={v => setF({ ...f, savings: v })} />
        <Field label="Housing (rent/mortgage)" prefix="$" value={f.housing} onChange={v => setF({ ...f, housing: v })} />
        <Field label="Food & Groceries" prefix="$" value={f.food} onChange={v => setF({ ...f, food: v })} />
        <Field label="Transportation" prefix="$" value={f.transport} onChange={v => setF({ ...f, transport: v })} />
        <Field label="Utilities" prefix="$" value={f.utilities} onChange={v => setF({ ...f, utilities: v })} />
        <Field label="Insurance" prefix="$" value={f.insurance} onChange={v => setF({ ...f, insurance: v })} />
        <Field label="Subscriptions" prefix="$" value={f.subscriptions} onChange={v => setF({ ...f, subscriptions: v })} />
        <Field label="Entertainment" prefix="$" value={f.entertainment} onChange={v => setF({ ...f, entertainment: v })} />
        <Field label="Other Spending" prefix="$" value={f.other} onChange={v => setF({ ...f, other: v })} />
      </div>
      <div style={styles.returnHints}>
        <span style={styles.hint}>10% = traditional goal · 20%+ = ahead of the curve · 50%+ = FIRE track</span>
      </div>
      <button style={styles.btn} onClick={calc}>Calculate</button>
      {r && (
        <div style={styles.result}>
          <BigNum color={r.savingsRate >= 20 ? '#22c55e' : r.savingsRate >= 10 ? '#f59e0b' : '#f87171'}>{r.savingsRate}%</BigNum>
          <p style={styles.desc}>Your monthly savings rate</p>
          <div style={styles.breakdown}>
            <span>💸 Monthly spending: <strong>{fmt(r.totalSpending)}</strong></span>
            <span>💰 Monthly savings: <strong style={{ color: '#22c55e' }}>{fmt(f.savings)}</strong></span>
            {r.leftover !== 0 && <span>↔️ Unaccounted: <strong style={{ color: r.leftover < 0 ? '#f87171' : '#64748b' }}>{fmt(r.leftover)}</strong></span>}
          </div>
          {r.yearsToFI && r.yearsToFI < 100 && (
            <div style={{ marginTop: '12px', fontSize: '13px', color: '#a78bfa' }}>
              ✨ At this rate, you could reach financial independence in approximately <strong>{r.yearsToFI} years</strong> (7% annual return, 4% withdrawal rule)
            </div>
          )}
        </div>
      )}
    </CalcCard>
  );
}

// ── COLLEGE FUND ──────────────────────────────────────────────────────────────
export function CollegeFundCalc() {
  const [f, setF] = useState({ childAge: 2, collegeStartAge: 18, currentSavings: 0, monthlyContribution: 200, annualReturn: 7, collegeCostPerYear: 35000 });
  const [r, setR] = useState(null);

  const calc = () => {
    const yearsToCollege = f.collegeStartAge - f.childAge;
    if (yearsToCollege <= 0) { setR({ error: 'College start age must be greater than child\'s current age.' }); return; }
    const monthlyRate = f.annualReturn / 100 / 12;
    const n = yearsToCollege * 12;
    // Future value of current savings
    const fvSavings = f.currentSavings * Math.pow(1 + monthlyRate, n);
    // Future value of monthly contributions
    const fvContribs = f.monthlyContribution * (Math.pow(1 + monthlyRate, n) - 1) / monthlyRate;
    const projected = fvSavings + fvContribs;
    // Total college cost (4 years, 5% annual inflation on tuition)
    let totalCost = 0;
    for (let i = 0; i < 4; i++) {
      totalCost += f.collegeCostPerYear * Math.pow(1.05, yearsToCollege + i);
    }
    const gap = totalCost - projected;
    const coveragePct = Math.min(100, Math.round((projected / totalCost) * 100));
    const neededMonthly = gap > 0 ? Math.round(gap / ((Math.pow(1 + monthlyRate, n) - 1) / monthlyRate)) : 0;
    setR({ projected, totalCost, gap, coveragePct, yearsToCollege, neededMonthly });
  };

  return (
    <CalcCard icon="🎓" title="College Fund Calculator" desc="How much do you need to save to cover college costs?">
      <div style={styles.grid}>
        <Field label="Child's Current Age" suffix="yrs" value={f.childAge} onChange={v => setF({ ...f, childAge: v })} />
        <Field label="College Start Age" suffix="yrs" value={f.collegeStartAge} onChange={v => setF({ ...f, collegeStartAge: v })} />
        <Field label="Current 529 / Savings" prefix="$" value={f.currentSavings} onChange={v => setF({ ...f, currentSavings: v })} />
        <Field label="Monthly Contribution" prefix="$" value={f.monthlyContribution} onChange={v => setF({ ...f, monthlyContribution: v })} />
        <Field label="Expected Annual Return" suffix="%" value={f.annualReturn} onChange={v => setF({ ...f, annualReturn: v })} />
        <Field label="Estimated Annual Tuition" prefix="$" value={f.collegeCostPerYear} onChange={v => setF({ ...f, collegeCostPerYear: v })} />
      </div>
      <div style={styles.returnHints}>
        <span style={styles.hint}>Avg 4yr public college: ~$25k/yr · Private: ~$55k/yr (2025)</span>
        <span style={styles.hint}>529 plans grow tax-free for education</span>
      </div>
      <button style={styles.btn} onClick={calc}>Calculate</button>
      {r && !r.error && (
        <div style={styles.result}>
          <BigNum color={r.coveragePct >= 100 ? '#22c55e' : r.coveragePct >= 75 ? '#f59e0b' : '#f87171'}>{r.coveragePct}%</BigNum>
          <p style={styles.desc}>Of projected college cost covered in {r.yearsToCollege} years</p>
          <div style={styles.breakdown}>
            <span>🎓 Projected savings: <strong style={{ color: '#22c55e' }}>{fmt(r.projected)}</strong></span>
            <span>📚 Estimated total cost (4 yrs, w/ inflation): <strong>{fmt(r.totalCost)}</strong></span>
            {r.gap > 0 && <span>⚠️ Gap: <strong style={{ color: '#f87171' }}>{fmt(r.gap)}</strong></span>}
          </div>
          {r.gap > 0 && r.neededMonthly > 0 && (
            <div style={{ marginTop: '12px', fontSize: '13px', color: '#f59e0b' }}>
              💡 To fully cover costs, increase monthly savings by <strong>{fmt(r.neededMonthly)}/mo</strong> (total: {fmt(f.monthlyContribution + r.neededMonthly)}/mo)
            </div>
          )}
          {r.gap <= 0 && (
            <div style={{ marginTop: '12px', fontSize: '13px', color: '#22c55e' }}>
              ✅ You're on track to fully cover college costs!
            </div>
          )}
        </div>
      )}
      {r?.error && <div style={styles.errMsg}>{r.error}</div>}
    </CalcCard>
  );
}

function makeStyles(isMobile = false) {
  return {
    page: { background: '#0f172a', color: '#f8fafc', minHeight: '100vh', padding: isMobile ? '48px 16px' : '60px 40px', maxWidth: '900px', margin: '0 auto', position: 'relative' },
    glow: { position: 'absolute', top: '-60px', right: '-80px', width: '500px', height: '500px', background: 'radial-gradient(ellipse at center, rgba(56,189,248,0.07) 0%, transparent 60%)', pointerEvents: 'none', zIndex: 0 },
    header: { position: 'relative', zIndex: 1, marginBottom: '40px' },
    title: { fontSize: '40px', fontWeight: '800', marginBottom: '8px', letterSpacing: '-0.5px' },
    subtitle: { color: '#64748b', fontSize: '16px', margin: 0 },
    card: { background: '#1e293b', borderRadius: '16px', padding: '32px', marginBottom: '24px', border: '1px solid #334155', position: 'relative', zIndex: 1 },
    cardHead: { display: 'flex', alignItems: 'flex-start', gap: '14px', marginBottom: '24px' },
    cardIcon: { fontSize: '28px', flexShrink: 0, marginTop: '2px' },
    cardTitle: { fontSize: '20px', fontWeight: '700', margin: '0 0 4px' },
    cardDesc: { color: '#64748b', fontSize: '14px', margin: 0 },
    grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px', marginBottom: '16px' },
    field: { display: 'flex', flexDirection: 'column', gap: '6px' },
    label: { fontSize: '11px', color: '#64748b', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' },
    inputWrap: { position: 'relative', display: 'flex', alignItems: 'center' },
    addon: { position: 'absolute', left: '10px', color: '#64748b', fontSize: '14px', pointerEvents: 'none' },
    suffix: { position: 'absolute', right: '10px', color: '#64748b', fontSize: '13px', pointerEvents: 'none' },
    input: { width: '100%', background: '#0f172a', border: '1px solid #334155', borderRadius: '10px', color: '#f8fafc', padding: '10px 12px', fontSize: '15px', boxSizing: 'border-box', fontFamily: 'inherit', outline: 'none' },
    returnHints: { display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '16px' },
    hint: { color: '#475569', fontSize: '12px', background: 'rgba(255,255,255,0.03)', padding: '4px 10px', borderRadius: '6px', border: '1px solid #1e293b' },
    btn: { background: '#38bdf8', color: '#0f172a', border: 'none', padding: '12px 28px', borderRadius: '10px', fontWeight: '700', fontSize: '15px', cursor: 'pointer', fontFamily: 'inherit' },
    result: { marginTop: '24px', background: '#0f172a', borderRadius: '12px', padding: '24px', textAlign: 'center', border: '1px solid rgba(56,189,248,0.15)' },
    bigNum: { fontSize: '52px', fontWeight: '800', lineHeight: 1, marginBottom: '8px' },
    desc: { color: '#94a3b8', fontSize: '15px', margin: '0 0 12px' },
    breakdown: { display: 'flex', gap: '20px', justifyContent: 'center', flexWrap: 'wrap', fontSize: '13px', color: '#64748b' },
    errMsg: { marginTop: '16px', color: '#f87171', fontSize: '14px', background: 'rgba(239,68,68,0.08)', padding: '10px 14px', borderRadius: '8px' },
    sectionLabel: { fontSize: '12px', fontWeight: '700', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '12px', marginTop: '8px' },
  };
}

const styles = makeStyles();

// ── MAIN ──────────────────────────────────────────────────────────────────────
function Calculators() {
  const isMobile = useIsMobile();
  const styles = makeStyles(isMobile);

  return (
    <div style={styles.page}>
      <SEO
        title="Free Financial Calculators — Retirement, Debt, Mortgage, Net Worth & More"
        description="9 free financial calculators: retirement savings, debt payoff, mortgage, net worth, savings rate, college fund, investment growth, compound interest, and emergency fund."
        path="/calculators"
        keywords="retirement calculator, debt payoff calculator, mortgage calculator, net worth calculator, compound interest calculator"
      />
      <div style={styles.glow} />
      <div style={styles.header}>
        <h1 style={styles.title}>Financial Calculators</h1>
        <p style={styles.subtitle}>Enter your numbers. See your future. 9 calculators covering every major financial decision.</p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center', marginTop: 16 }}>
          {[
            { id: 'retirement', label: '🏖️ Retirement' },
            { id: 'debt', label: '💳 Debt Payoff' },
            { id: 'mortgage', label: '🏠 Mortgage' },
            { id: 'net-worth', label: '📊 Net Worth' },
            { id: 'savings-rate', label: '💰 Savings Rate' },
            { id: 'college-fund', label: '🎓 College Fund' },
            { id: 'investment', label: '📈 Investment' },
            { id: 'compound-interest', label: '✨ Compound Interest' },
            { id: 'emergency-fund', label: '🛡️ Emergency Fund' },
          ].map(c => (
            <Link key={c.id} to={`/calculators/${c.id}`} style={{ background: 'rgba(56,189,248,.08)', border: '1px solid rgba(56,189,248,.2)', borderRadius: 20, padding: '5px 14px', fontSize: 13, color: '#38bdf8', textDecoration: 'none', fontWeight: 500 }}>
              {c.label}
            </Link>
          ))}
        </div>
      </div>
      <RetirementCalc />
      <DebtCalc />
      <MortgageCalc />
      <NetWorthCalc />
      <SavingsRateCalc />
      <CollegeFundCalc />
      <InvestmentCalc />
      <CompoundCalc />
      <EmergencyCalc />
      <EmailCapture
        variant="inline"
        source="calculators-bottom"
        title="Want your results emailed?"
        subtitle="Subscribe to the Brief and we'll add email reports soon."
      />
    </div>
  );
}


export default Calculators;
